import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UserRepository } from '@data/user/user.store';
import { AppRouteNames } from '../../app.routing.names';
import { ErrorRouteNames } from '../../modules/error/error.routing.names';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
// APP_CONFIG lives in app.module (DI/factory hub); importing it here matches the
// pattern used across the app. Relocating the token is out of scope for core.
import { APP_CONFIG } from '@core/config/app-config.token';
import { HomeRouteNames } from '../../modules/home/home.routing.names';
import { Logger } from '@shared/util/logger.util';
import { externalUserId } from '@core/services/kiosk/kiosk';

export const authGuard: CanActivateFn = () => {
  const userRepo = inject(UserRepository);
  const router = inject(Router);
  const appConfig = inject(APP_CONFIG);

  // A live in-memory user means an active session; on a 401 the interceptor clears
  // it (UserRepository.clearSession), so a dead token forces a re-bootstrap here.
  if (userRepo.user?.id) return true;

  const storedUserId = window.localStorage.getItem('userId');
  if (!storedUserId && appConfig.modules.home.loginViaShortId) {
    router.navigate([AppRouteNames.HOME, HomeRouteNames.PSEUDO_LOGIN]);
    return false;
  }

  // Only elearning-embed fairs and embedded job apps accept an external user id from the URL
  // (`?userId=`, moved out of the address at startup).
  const externalId = appConfig.app.allowExternalIdLogin ? externalUserId() : null;

  // loginUser resolves the right bootstrap: authenticated load (token present),
  // legacy-session migration (id but no token), external-id exchange, or fresh user.
  return userRepo.loginUser(externalId).pipe(
    map((data) => {
      if (Array.isArray(data) && !!data[0]?.id) {
        return true;
      }
      // A swallowed 401 is already handled by the interceptor (token cleared +
      // redirect home); only fall through to 404 on a genuine empty result.
      if (!(data instanceof HttpErrorResponse)) {
        router.navigate([AppRouteNames.ERROR, ErrorRouteNames.ERROR404], {
          queryParamsHandling: 'merge',
        });
      }
      return false;
    }),
    catchError((error) => {
      Logger.error(error);
      return of(false);
    }),
  );
};
